import * as React from 'react';
import { ReactNode } from 'react';
import { AutoSizer } from 'react-virtualized';
import Box from '@mui/material/Box';
import { IWidgetSize, calculateGridLayout } from './Utils/EDDashboardUtils';

/**
 * Props for the EDDashboard component
 */
export interface IProps {
  /** Widget components to arrange in the dashboard grid */
  children: ReactNode;
  /** Optional size configurations for each widget (spanColumns, spanRows) */
  widgetSizes?: IWidgetSize[];
  /** Minimum width for grid items in pixels (default: 200) */
  minItemWidth?: number;
  /** Maximum width for grid items in pixels (default: 300) */
  maxItemWidth?: number;
  /** Minimum height for grid items in pixels (default: 300) */
  minItemHeight?: number;
  /** Maximum height for grid items in pixels (default: 400) */
  maxItemHeight?: number;
  /** Space between grid items in pixels (default: 16) */
  gap?: number;
  /** Force single-column mobile layout regardless of screen size */
  forceSmallScreen?: boolean;
}

/**
 * Responsive dashboard component that arranges child widgets in an optimal grid layout.
 * Supports widget spanning across multiple columns/rows and automatically adapts to screen size.
 */
export const EDDashboard: React.FC<IProps> = ({
  children,
  widgetSizes,
  minItemWidth = 200,
  maxItemWidth = 300,
  minItemHeight = 300,
  maxItemHeight = 400,
  gap = 16,
  forceSmallScreen = false,
}) => {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        boxSizing: 'border-box',
      }}
    >
      <AutoSizer>
        {({ width, height }: { width: number; height: number }) => {
          const { itemWidth, itemHeight, totalWidth, totalHeight, grid } =
            calculateGridLayout(
              width - gap,
              height - gap,
              React.Children.count(children) ?? 0,
              widgetSizes,
              minItemWidth,
              maxItemWidth,
              minItemHeight,
              maxItemHeight,
              gap,
              forceSmallScreen
            );

          return (
            <Box
              sx={{
                width: `${totalWidth + gap}px`,
                height: `${totalHeight + gap}px`,
                left: `${
                  width > totalWidth + gap
                    ? (width - (totalWidth + gap)) / 2
                    : 0
                }px`,
                top: 0,
                position: 'absolute',
                boxSizing: 'border-box',
              }}
            >
              {React.Children.map(children, (child, index) => {
                const position = grid[index];
                if (!position || position.spanCols < 1 || position.spanRows < 1)
                  return null;

                const left = position.col * (itemWidth + gap);
                const top = position.row * (itemHeight + gap);
                const width =
                  position.spanCols * itemWidth + (position.spanCols - 1) * gap;
                const height =
                  position.spanRows * itemHeight +
                  (position.spanRows - 1) * gap;

                return (
                  <Box
                    key={index}
                    sx={{
                      left: `${left + gap / 2}px`,
                      top: `${top + gap / 2}px`,
                      width: `${width}px`,
                      height: `${height}px`,
                      position: 'absolute',
                      boxSizing: 'border-box',
                    }}
                  >
                    {child}
                  </Box>
                );
              })}
            </Box>
          );
        }}
      </AutoSizer>
    </Box>
  );
};
